/* oxlint-disable typescript/no-deprecated */
import { useGesture } from '@use-gesture/react';
import clsx from 'clsx';
import {
  forwardRef,
  type HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';

import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { KeepMountedProvider } from './keepMounted.js';
import type { PanelName } from './page-types.js';
import { panelContext } from './panel-context.js';
import type { PanelSize } from './panel-types.js';
import * as styles from './panel.sty.js';

interface PanelRootProps
  extends WithChildren, StylingProps, HTMLAttributes<HTMLDivElement> {
  name: PanelName;
}

const PanelRoot = (props: PanelRootProps) => {
  const { className, id, style, children, name }: PanelRootProps = props;

  const ctx = useMemo(() => ({ name }), [name]);

  return (
    <panelContext.Provider value={ctx}>
      <KeepMountedProvider>
        <aside
          {...props}
          id={id}
          className={clsx(styles.root, className)}
          style={style}
        >
          {children}
        </aside>
      </KeepMountedProvider>
    </panelContext.Provider>
  );
};

interface PanelContentProps extends WithChildren, StylingProps {
  isDismissed?: boolean; // To retain the size when dismissing and expanding the panel, we need to handle the dismissed display mode inside
  /** @defaultValue `right` */
  handle?: 'left' | 'right' | 'hidden';
  /** Descriptive label for the panel. */
  label: string;
  /**
   * The minimum width of the sidebar in pixels.
   * @defaultValue 200
   */
  minWidth?: number;
  /**
   * The desired width of the sidebar.
   * Depending on the available screen width and other panel configurations,
   * the size of the panel might not match the `preferredWidth` exactly.
   * @defaultValue 15%
   */
  preferredWidth?: PanelSize;
  /** Callback triggered after resizing is finished. */
  onResize?: (width: number) => void;
  /** Available size of the containing element. Used to calculate size with relative units. */
  containerSize: number;
}

const PanelContent = forwardRef<HTMLDivElement, PanelContentProps>(
  (props, forwardedRef) => {
    const {
      children,
      className,
      handle = 'right',
      isDismissed,
      label,
      minWidth,
      onResize,
      preferredWidth,
      style,
      containerSize,
      ...restProps
    }: PanelContentProps = props;

    const panelRef = useRef<HTMLDivElement>(null);
    const panelRefs = useMergeRefs([panelRef, forwardedRef]);

    const handleRef = useRef<HTMLHRElement>(null);

    const draggedSize = useRef<number>(undefined);
    const wasResized = useRef(false);

    const setPanelSize = useCallback(
      (size: number) => {
        if (!panelRef.current) {
          return;
        }

        let sizeToApply = Math.floor(size * 1000) / 1000;
        draggedSize.current = sizeToApply;

        // users can resize out of bounds and create negative size values. we dont want to apply those as css
        if (size < 0) {
          return;
        }

        if (minWidth && sizeToApply < minWidth) {
          sizeToApply = minWidth;
        }

        panelRef.current.style.setProperty('width', `${sizeToApply}px`);
      },
      [minWidth],
    );

    const ensurePanelSizeMatchesComputedWidth = useCallback(
      (element: HTMLElement, size: number) => {
        const actualWidth = parseFloat(getComputedStyle(element).width);
        if (size !== actualWidth) {
          setPanelSize(actualWidth);
        }
        return actualWidth;
      },
      [setPanelSize],
    );

    const handleDrag = useCallback(
      (deltaX: number) => {
        if (!draggedSize.current) {
          return;
        }
        setPanelSize(
          draggedSize.current + deltaX * (handle === 'right' ? 1 : -1),
        );
        wasResized.current = true;
      },
      [handle, setPanelSize],
    );

    const handleDragEnd = useCallback(() => {
      if (!panelRef.current || !draggedSize.current) {
        return;
      }
      const size = ensurePanelSizeMatchesComputedWidth(
        panelRef.current,
        draggedSize.current,
      );
      onResize?.(size);
    }, [ensurePanelSizeMatchesComputedWidth, onResize]);

    const handleDragStart = useCallback(() => {
      if (!panelRef.current || !draggedSize.current) {
        return;
      }
      ensurePanelSizeMatchesComputedWidth(
        panelRef.current,
        draggedSize.current,
      );
    }, [ensurePanelSizeMatchesComputedWidth]);

    useGesture(
      {
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
        onDrag: ({ delta: [deltaX] }) => handleDrag(deltaX),
      },
      {
        target: handleRef,
        eventOptions: { passive: false },
        drag: { filterTaps: true },
      },
    );

    useEffect(
      function initializePanelSize() {
        if (!panelRef.current || wasResized.current || isDismissed) {
          return;
        }

        if (typeof preferredWidth === 'number') {
          setPanelSize(preferredWidth);
          return;
        }

        // percentage based values on grid children use the available space of the area, not the full grid container
        if (preferredWidth?.includes('%')) {
          const size = containerSize * (parseFloat(preferredWidth) / 100);
          setPanelSize(size);
          return;
        }

        if (preferredWidth?.includes('px')) {
          const size = parseFloat(preferredWidth);
          setPanelSize(size);
          return;
        }

        // for all other units we let the browser compute the size
        if (typeof preferredWidth === 'string') {
          panelRef.current.style.setProperty('width', preferredWidth);
          const size = parseFloat(getComputedStyle(panelRef.current).width);
          setPanelSize(size);
          return;
        }

        const size = parseFloat(getComputedStyle(panelRef.current).width);
        setPanelSize(size);
      },
      [containerSize, preferredWidth, setPanelSize, isDismissed],
    );

    if (isDismissed) {
      return null;
    }

    return (
      <>
        <div
          {...restProps}
          ref={panelRefs}
          className={clsx(styles.panelContent, className)}
          style={{ ...style, minWidth, width: draggedSize.current }}
        >
          {children}
        </div>

        <div className={styles.resizeHandleContainer}>
          {handle !== 'hidden' ? (
            <ResizeHandle
              panelName={label}
              panelSize={draggedSize.current}
              ref={handleRef}
            />
          ) : null}
        </div>
      </>
    );
  },
);

interface ResizeHandleProps {
  panelName: string;
  panelSize?: number;
}

const ResizeHandle = forwardRef<HTMLHRElement, ResizeHandleProps>(
  (props, ref) => {
    const { panelName, panelSize = 0 } = props;

    const intl = useIntl();
    const { focusProps, focusClassName } = useFocusRing({
      focusWithin: true,
      ignoreModality: true,
    });

    return (
      <hr
        {...focusProps}
        ref={ref}
        className={clsx(styles.resizeHandle, focusClassName)}
        aria-valuenow={panelSize}
        aria-valuetext={`${Math.round(panelSize)}px`}
        aria-orientation="vertical"
        aria-label={intl.formatMessage(
          {
            defaultMessage: 'Resize {label} panel',
            id: 'Ym3RUHtaLVFsGlaM',
            description:
              'Label for the resize handle of the panel to resize it with drag and drop or keyboard.',
          },
          { label: panelName },
        )}
        tabIndex={0}
        data-dt-name="Resize"
      />
    );
  },
);

export const Panel = Object.assign(PanelRoot, {
  Content: PanelContent,
});
